import React, { useState, useEffect, useRef } from "react";
import { logResponse } from "../utils/logger";
import { fetchRegisterNewCustomer } from "../api";
import { useNavigate } from "react-router-dom";
import SubmitButton from "../components/SubmitButton";

const prefectures = [
  "北海道", "青森県", "岩手県", "宮城県", "秋田県", "山形県", "福島県",
  "茨城県", "栃木県", "群馬県", "埼玉県", "千葉県", "東京都", "神奈川県",
  "新潟県", "富山県", "石川県", "福井県", "山梨県", "長野県", "岐阜県",
  "静岡県", "愛知県", "三重県", "滋賀県", "京都府", "大阪府", "兵庫県",
  "奈良県", "和歌山県", "鳥取県", "島根県", "岡山県", "広島県", "山口県",
  "徳島県", "香川県", "愛媛県", "高知県", "福岡県", "佐賀県", "長崎県",
  "熊本県", "大分県", "宮崎県", "鹿児島県", "沖縄県"
];
const eras = [
  { name: "令和", start: 2019 },
  { name: "平成", start: 1989 },
  { name: "昭和", start: 1926 },
  { name: "大正", start: 1912 },
];

const NewRegist = () => {
  const formRef = useRef();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isSubmittingRef = useRef(false);

  const months = Array.from({ length: 12 }, (_, i) => i + 1);
  const days = Array.from({ length: 31 }, (_, i) => i + 1);

  const [lastName, setLastName] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastKana, setLastKana] = useState("");
  const [firstKana, setFirstKana] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [customerKana, setCustomerKana] = useState("");

  const [year, setYear] = useState("");
  const [month, setMonth] = useState("");
  const [day, setDay] = useState("");
  const [birthDate, setBirthDate] = useState("");

  const [postalCode, setPostalCode] = useState("");
  const [prefecture, setPrefecture] = useState("");
  const [city, setCity] = useState("");
  const [addressNumber, setAddressNumber] = useState("");
  const [addressDetail, setAddressDetail] = useState("");
  const [address, setAddress] = useState("");
  const cityRef = useRef(null);

  const [tel1, setTel1] = useState("");
  const [tel2, setTel2] = useState("");
  const [tel3, setTel3] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");

  const [leadSource, setLeadSource] = useState("");
  const [showOtherTextarea, setShowOtherTextarea] = useState(false);
  const [otherText, setOtherText] = useState("");

  const [accessToken, setAccessToken] = useState("");
  const [isReady, setIsReady] = useState(false);
  const [errors, setErrors] = useState({});


  // 和暦を表示する
  const generateYearOptions = () => {
    const currentYear = new Date().getFullYear();
    const startYear = currentYear - 106;
    const endYear = currentYear - 19;

    const options = [];

    for (let y = startYear; y <= endYear; y++) {
      const era = eras.find((e) => y >= e.start);
      const eraYear = y - era.start + 1;
      const label = `${y}（${era.name}${eraYear === 1 ? "元" : eraYear}）年`;
      options.push({ value: y.toString(), label });
    }

    return options;
  };
  const yearOptions = generateYearOptions();

  // 氏名、カナ結合
  useEffect(() => {
    setCustomerName(`${lastName}${firstName}`);
  }, [lastName, firstName]);

  useEffect(() => {
    setCustomerKana(`${lastKana}${firstKana}`);
  }, [lastKana, firstKana]);

  // 生年月日結合
  useEffect(() => {
    if (year && month && day) {
      const mm = String(month).padStart(2, "0");
      const dd = String(day).padStart(2, "0");
      setBirthDate(`${year}${mm}${dd}`);
    } else {
      setBirthDate("");
    }
  }, [year, month, day]);

  // 住所の結合
  useEffect(() => {
    setAddress(`${prefecture}${city}${addressNumber}${addressDetail}`);
  }, [prefecture, city, addressNumber, addressDetail]);

  // 電話番号結合
  useEffect(() => {
    setPhoneNumber(`${tel1}-${tel2}-${tel3}`);
  }, [tel1, tel2, tel3]);

  // メディア結合
  const handleMediaChange = (nextText = otherText) => {
    const selected = Array.from(formRef.current.querySelectorAll("input[name='locate[]']:checked"))
      .map((el) => el.value);

    const trimmed = typeof nextText === "string" ? nextText.trim() : "";
    if (trimmed) {
      selected.push(`その他：${trimmed}`);
    }

    setLeadSource(selected.join(","));
  };

  // その他変更時もメディア結合を呼ぶ
  const handleOtherTextChange = (e) => {
    const nextText = e.target.value;
    setOtherText(nextText);
    handleMediaChange(nextText); // ← 最新の値を明示的に渡す
  };
  
  // メディアアンケート表示切り替え
  useEffect(() => {
    const parents = document.querySelectorAll(".locate-parent input[type='checkbox']");
    parents.forEach((checkbox) => {
      const parent = checkbox.closest(".locate-parent");
      const child = parent?.nextElementSibling;
      if (!child || !child.classList.contains("locate-child")) return;

      const toggle = () => {
        child.style.display = checkbox.checked ? "block" : "none";
      };

      checkbox.addEventListener("change", toggle);
      toggle(); // 初期表示

      // クリーンアップ
      return () => {
        checkbox.removeEventListener("change", toggle);
      };
    });
  }, [isReady]);

  // 郵便番号 → 住所補完
  const handlePostalCodeChange = (e) => {
    const value = e.target.value;
    setPostalCode(value);

    const cleanZip = value.replace(/[^0-9]/g, "");
    if (cleanZip.length === 7) {
      fetch(`https://zipcloud.ibsnet.co.jp/api/search?zipcode=${cleanZip}`)
        .then((res) => res.json())
        .then((data) => {
          if (data.results && data.results.length > 0) {
            const result = data.results[0];
            setPrefecture(result.address1);
            setCity(result.address2 + result.address3);

            // 7桁入力したら市区町村にフォーカス
            setTimeout(() => {
              cityRef.current?.focus();
            }, 0);
          }
        })
        .catch(() => {
          setPrefecture("");
          setCity("");
        });
    }
  };

  // アクセストークン取得
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tokenFromUrl = params.get("token");
    setAccessToken(tokenFromUrl || "");
    setIsReady(true);
  }, []);

  // 送信処理
  const handleClick = (event) => {
    event.preventDefault();
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    setIsSubmitting(true);

    const newErrors = {};
    if (!accessToken) {
      setErrors({ general: "アクセストークンがありません。メールからアクセスしてください。" });
      return;
    }
    setErrors({});

    const formData = new FormData(formRef.current);
    formData.set("access_token", accessToken);
    formData.set("customer_name", customerName);
    formData.set("customer_kana", customerKana);
    formData.set("birth_date", birthDate);
    formData.set("address", address);
    formData.set("phone_number", phoneNumber);
    formData.set("lead_source", leadSource);
    const data = Object.fromEntries(formData.entries());

    // 氏名（必須＋全角チェック）
    if (!lastName) {
      newErrors.last_name = "姓は必須です";
    } else if (!/^[^\u0020-\u007E\uFF61-\uFF9F]+$/.test(lastName)) {
      newErrors.last_name = "姓は全角で入力してください";
    }

    if (!firstName) {
      newErrors.first_name = "名は必須です";
    } else if (!/^[^\u0020-\u007E\uFF61-\uFF9F]+$/.test(firstName)) {
      newErrors.first_name = "名は全角で入力してください";
    }

    // フリガナ（必須＋全角カナチェック）
    if (!lastKana) {
      newErrors.last_kana = "セイは必須です";
    } else if (!/^[\u30A0-\u30FFー]+$/.test(lastKana)) {
      newErrors.last_kana = "セイは全角カタカナで入力してください";
    }
    if (!firstKana) {
      newErrors.first_kana = "メイは必須です";
    } else if (!/^[\u30A0-\u30FFー]+$/.test(firstKana)) {
      newErrors.first_kana = "メイは全角カタカナで入力してください";
    }

    // 生年月日（セレクト必須）
    if (!year || !month || !day) {
      newErrors.birth_date = "生年月日を選択してください";
    }

    // 郵便番号（必須＋7桁数字）
    if (!postalCode) {
      newErrors.postal_code = "郵便番号は必須です";
    } else if (!/^\d{3}-?\d{4}$/.test(postalCode)) {
      newErrors.postal_code = "郵便番号は半角数字7桁（例：1234567 または 123-4567）で入力してください";
    }

    // 都道府県・市区町村・番地（必須）
    if (!prefecture) {
      newErrors.prefecture_name = "都道府県を選択してください";
    }
    if (!city) {
      newErrors.municipality_name = "市区町村は必須です";
    }
    if (!addressNumber) {
      newErrors.address_number = "丁目・番地は必須です";
    }

    // 電話番号（必須＋数字チェック）
    if (!tel1 || !tel2 || !tel3) {
      newErrors.phone_number = "電話番号は必須です";
    } else if (!/^\d+$/.test(tel1 + tel2 + tel3)) {
      newErrors.phone_number = "電話番号は半角数字で入力してください";
    }

    // メディア（1つ以上必須）
    const selectedMedia = Array.from(formRef.current.querySelectorAll("input[name='locate[]']:checked"));
    if (selectedMedia.length === 0) {
      newErrors.locate = "メディアは1つ以上選択してください";
    }

    // 同意チェック
    const agreed = formRef.current.querySelector("input[name='privacy_agree']:checked");
    if (!agreed || agreed.value !== "同意する") {
      newErrors.privacy_agree = "個人情報の取り扱いに同意してください";
    }

    // エラーがあれば表示して終了
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      isSubmittingRef.current = false;
      setIsSubmitting(false);
      return;
    }

    setErrors({});

    fetchRegisterNewCustomer(accessToken, data)
      .then((res) => {
        logResponse("token:", accessToken);
        logResponse("req:", data);
        logResponse("res:", res);
        if (res && res.message_id) {
          setTimeout(() => {
            navigate("/signup-thanks");
          }, 0);
        } else if (res && res.detail) {
          const message =
            res.detail === "Not authenticated"
              ? "アクセストークンがありません。メールからアクセスしてください。"
              : res.detail;
          setErrors({ general: message });
        } else {
          setErrors({ general: "登録に失敗しました。時間を置いて再度お試しください。" });
        }
        isSubmittingRef.current = false;
        setIsSubmitting(false);
      })
      .catch((err) => {
        setErrors({ general: "通信エラーが発生しました。時間を置いて再度お試しください。" });
        isSubmittingRef.current = false;
        setIsSubmitting(false);
      });
  };

  if (!isReady) return null;

  // アクセストークンがない場合のエラー表示
  if (!accessToken) {
    return (
      <p className="alert alert-danger tc" role="alert">
        アクセストークンが見つかりません。<br />
        メールのリンクからアクセスしてください。<br />
        <a className="underline" href="/">トップページへ戻る</a>
      </p>
    );
  }

  return (
    <section className="section regist-section">
      <div className="form-group form-type-2">
        <div className="box-white">
          <form ref={formRef}>
          <p className="explain error tc fs100">
            すでに入力が済んでいる場合は、再入力不要です。
            <br />
            数営業日以内にログインが可能となりますので、それまでお待ちくださいませ。
          </p>

          {/* 氏名 */}
          <div>
            <span className="column mt0">お名前<span className="require">必須</span></span>
            <div className="flex between">
              <input className="harf" type="text" name="last_name" placeholder="姓" value={lastName} onChange={(e) => setLastName(e.target.value)} required />
              <input className="harf" type="text" name="first_name" placeholder="名" value={firstName} onChange={(e) => setFirstName(e.target.value)} required />
            </div>
              {errors.last_name && <div className="error">{errors.last_name}</div>}
              {errors.first_name && <div className="error">{errors.first_name}</div>}
          </div>

          {/* フリガナ */}
          <div>
            <span className="column">お名前（フリガナ）<span className="require">必須</span></span>
            <div className="flex between">
              <input className="harf" type="text" name="last_kana" placeholder="セイ" value={lastKana} onChange={(e) => setLastKana(e.target.value)} required />
              <input className="harf" type="text" name="first_kana" placeholder="メイ" value={firstKana} onChange={(e) => setFirstKana(e.target.value)} required />
            </div>
            {errors.last_kana && <div className="error">{errors.last_kana}</div>}
            {errors.first_kana && <div className="error">{errors.first_kana}</div>}
          </div>

            {/* 性別 */}
            <div>
              <span className="column">性別<span className="require">必須</span></span>
              <div className="flex left">
                <label className="radio"><input type="radio" name="gender_type" value="女性" defaultChecked />女性</label>
                <label className="radio"><input type="radio" name="gender_type" value="男性" />男性</label>
              </div>
            </div>

            {/* 生年月日 */}
            <div>
              <span className="column">生年月日<span className="require">必須</span></span>
              <div className="birthday">
                <select name="birth-year" value={year} onChange={(e) => setYear(e.target.value)} required>
                  <option value="">年</option>
                  {yearOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                <select name="birth-month" value={month} onChange={(e) => setMonth(e.target.value)} required>
                  <option value="">月</option>
                  {months.map((m) => (
                    <option key={m} value={m}>{m}月</option>
                  ))}
                </select>
                <select name="birth-day" value={day} onChange={(e) => setDay(e.target.value)} required>
                  <option value="">日</option>
                  {days.map((d) => (
                    <option key={d} value={d}>{d}日</option>
                  ))}
                </select>
              </div>
              {errors.birth_date && <div className="error">{errors.birth_date}</div>}
            </div>

            {/* 郵便番号 */}
            <div>
              <span className="column">郵便番号<span className="require">必須</span></span>
              <input
                type="text"
                name="postal_code"
                value={postalCode}
                onChange={handlePostalCodeChange}
              />
              <p className="comment">(7ケタ半角数字　例：123-4567)</p>
              {errors.postal_code && <div className="error">{errors.postal_code}</div>}
            </div>

            {/* 住所 */}
            <div>
              <span className="column">ご住所<span className="require">必須</span></span>
            </div>

            <div>
              <span className="column small inline_block">都道府県</span>
              <select name="prefecture_name" value={prefecture} onChange={(e) => setPrefecture(e.target.value)}>
                <option value="">選択してください</option>
                {prefectures.map((pref) => <option key={pref} value={pref}>{pref}</option>)}
              </select>
              {errors.prefecture_name && <div className="error">{errors.prefecture_name}</div>}
            </div>

            <div>
              <span className="column small">市区町村</span>
              <input
                type="text"
                name="municipality_name"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                ref={cityRef}
              />
              {errors.municipality_name && <div className="error">{errors.municipality_name}</div>}
            </div>

            <div>
              <span className="column small">丁目・番地<span className="require">必須</span></span>
              <input
                type="text"
                name="address_number"
                value={addressNumber}
                onChange={(e) => setAddressNumber(e.target.value)}
                required
              />
              {errors.address_number && <div className="error">{errors.address_number}</div>}
            </div>

            <div>
              <span className="column small">マンション・ビル名・号数など</span>
              <input type="text" name="address_detail_1" value={addressDetail} onChange={(e) => setAddressDetail(e.target.value)} />
              {errors.address_detail && <div className="error">{errors.address_detail}</div>}
            </div>

            {/* 電話番号 */}
            <div>
              <span className="column">電話番号<span className="require">必須</span></span>
              <div className="flex left">
                <label className="radio"><input type="radio" name="phone_number_type" value="携帯" defaultChecked />携帯</label>
                <label className="radio"><input type="radio" name="phone_number_type" value="自宅" />自宅</label>
                <label className="radio"><input type="radio" name="phone_number_type" value="職場" />職場</label>
              </div>
            <input
              className="third"
              type="tel"
              name="tel1"
              value={tel1}
              onChange={(e) => setTel1(e.target.value)}
              maxLength={5}
            />
            -
            <input
              className="third"
              type="tel"
              name="tel2"
              value={tel2}
              onChange={(e) => setTel2(e.target.value)}
              maxLength={5}
            />
            -
            <input
              className="third"
              type="tel"
              name="tel3"
              value={tel3}
              onChange={(e) => setTel3(e.target.value)}
              maxLength={5}
            />
            {errors.phone_number && <div className="error">{errors.phone_number}</div>}
            </div>

            {/* メディアアンケート */}
            <div className="locates">
              <span className="column">メディア<span className="require">必須</span></span>
              <p className="comment">どのようなきっかけで当社をお知りいただけましたか？</p>
              <div className="checkbox" onChange={handleMediaChange}>
                <label><input type="checkbox" name="locate[]" value="紹介" />紹介</label>

                <label className="locate-parent"><input type="checkbox" name="locate[]" value="女性誌・情報誌" />女性誌・情報誌</label>
                <div className="locate-child">
                  <p className="locate-text">ご覧になった女性誌・情報誌をお選びください</p>
                  {["婦人公論", "クロワッサン", "ESSE", "サンキュ！", "パンプキン", "天然生活", "クウネル", "STORY", "週刊女性", "ゆうゆう", "anan", "美ST", "大人のおしゃれ手帖", "素敵なあの人", "マタニティSTYLE", "その他の女性誌・情報誌"].map((label) => (
                    <label key={label}><input type="checkbox" name="locate[]" value={label} />{label}</label>
                  ))}
                </div>

                <label className="locate-parent"><input type="checkbox" name="locate[]" value="ファッション誌" />ファッション誌</label>
                <div className="locate-child">
                  <p className="locate-text">ご覧になったファッション誌をお選びください</p>
                  {["美ST", "STORY", "大人百花", "anan", "大人のおしゃれ手帖", "素敵なあの人", "GLOW", "リンネル", "otona MUSE", "In Red", "その他のファッション誌"].map((label) => (
                    <label key={label}><input type="checkbox" name="locate[]" value={label} />{label}</label>
                  ))}
                </div>

                <label className="locate-parent"><input type="checkbox" name="locate[]" value="健康雑誌" />健康雑誌</label>
                <div className="locate-child">
                  <p className="locate-text">ご覧になった健康雑誌をお選びください</p>
                  {["veggy", "クロワッサン", "スコブル", "安心", "壮快", "ゆほびか", "からだにいいこと", "マタニティSTYLE", "その他の健康雑誌"].map((label) => (
                    <label key={label}><input type="checkbox" name="locate[]" value={label} />{label}</label>
                  ))}
                </div>

                <label><input type="checkbox" name="locate[]" value="その他の雑誌" />その他の雑誌</label>
                <label><input type="checkbox" name="locate[]" value="新聞" />新聞</label>
                <label><input type="checkbox" name="locate[]" value="チラシ" />チラシ</label>

                <label className="locate-parent"><input type="checkbox" name="locate[]" value="Webメディア・ネット記事" />Webメディア・ネット記事</label>
                <div className="locate-child">
                  <p className="locate-text">ご覧になったWebメディア・ネット記事をお選びください</p>
                  {["美ST", "キナリノ", "女子SPA！", "anan", "大人百花", "その他Webメディア・ネット記事"].map((label) => (
                    <label key={label}><input type="checkbox" name="locate[]" value={label} />{label}</label>
                  ))}
                </div>

                <label><input type="checkbox" name="locate[]" value="Web動画" />Web動画</label>
                <label><input type="checkbox" name="locate[]" value="Facebook" />Facebook</label>

                <label className="locate-parent"><input type="checkbox" name="locate[]" value="インスタグラム" />インスタグラム</label>
                <div className="locate-child">
                  <p className="locate-text">ご覧になったインスタグラムをお選びください</p>
                  {["公式アカウント", "LOCARI", "veggy", "大人百花", "インスタグラマー", "その他"].map((label) => (
                    <label key={label}><input type="checkbox" name="locate[]" value={label} />{label}</label>
                  ))}
                </div>

                <label><input type="checkbox" name="locate[]" value="ブログの書込み" />ブログの書込み</label>
                <label><input type="checkbox" name="locate[]" value="ネットでお悩み検索" />ネットでお悩み検索</label>
                <label className="locate_other">
                  <input
                    type="checkbox"
                    name="locate[]"
                    value="その他"
                    onChange={(e) => setShowOtherTextarea(e.target.checked)}
                  />
                  その他
                </label>
                {showOtherTextarea && (
                  <div className="locate_other_text">
                    <span className="column small">その他の内容をご記入ください</span>
                    <textarea
                      name="locate_other_text"
                      value={otherText}
                      onChange={handleOtherTextChange}
                      rows={3}
                      style={{ width: "100%" }}
                    />
                  </div>
                )}
              </div>
              {errors.locate && <div className="error">{errors.locate}</div>}
            </div>

            <label>
              <span className="column">ご紹介者名や具体的な雑誌名・新聞名、係名がお分かりになりましたらご記入下さい。</span>
              <textarea name="lead_source_remarks" rows={3} />
            </label>

            {/* 個人情報の取り扱い */}
            <div>
              <span className="column">個人情報の取り扱い<span className="require">必須</span></span>
              <p>
                言歩木のご利用にあたり、規約の内容に同意の上でご利用いただきますようお願いいたします。
                <br /><br />
                内容をご確認の上、同意される場合は、『
    <a
      href="https://www.kotohogi.co.jp/kojinjoho.html"
      target="_blank"
      rel="noopener noreferrer"
      style={{ textDecoration: "underline" }}
    >
      個人情報の取り扱い
    </a>
    』に同意するにチェックを入れてください。
              </p>
              <div className="tc checkbox">
                <label><input type="checkbox" name="privacy_agree" value="同意する" />同意する</label>
              </div>
              {errors.privacy_agree && <div className="error">{errors.privacy_agree}</div>}
            </div>

            {/* エラー文言 */}
            {errors.general && (
              <p className="text-danger bold" style={{ color: "red", marginBottom: "0.5em" }}>
                {errors.general}
              </p>
            )}

            {/* 送信ボタン */}
            <div className="submit btn-green">
              <SubmitButton
                type="button"
                onClick={handleClick}
                isSubmitting={isSubmitting}
                label="新規登録"
                className="btn-green"
                wrap={false}
              />
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};
export default NewRegist;
